import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import { toast } from 'sonner';
import { 
  Bell, Mail, Calendar, Receipt, FileText, AlertTriangle,
  Check, CheckCheck, Trash2, X, RefreshCw, BellRing, Settings, BellOff
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatDistanceToNow } from "date-fns";
import { it } from "date-fns/locale";
import notificationService from '../services/NotificationService';

const ICONE = {
  mail: Mail,
  calendar: Calendar,
  receipt: Receipt,
  file: FileText,
  alert: AlertTriangle,
  bell: Bell
};

const COLORI_PRIORITA = {
  urgente: 'bg-red-500',
  alta: 'bg-orange-500',
  normale: 'bg-blue-500',
  bassa: 'bg-gray-400'
};

const NotificheDropdown = () => {
  const navigate = useNavigate();
  const [notifiche, setNotifiche] = useState([]);
  const [count, setCount] = useState(0);
  const [emailCount, setEmailCount] = useState(0);
  const [emailCaselle, setEmailCaselle] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [hasNewEmail, setHasNewEmail] = useState(false);

  const loadNotifiche = useCallback(async () => {
    try {
      const [notRes, countRes, emailRes] = await Promise.all([
        api.get('/notifiche?limit=20'),
        api.get('/notifiche/count'),
        api.get('/notifications/new-emails')
      ]);
      setNotifiche(notRes.data);
      setCount(countRes.data.non_lette);
      setEmailCount(emailRes.data.new_count || 0);
      setEmailCaselle(emailRes.data.caselle || []);
      
      if (emailRes.data.new_count > 0) {
        setHasNewEmail(true);
      }
    } catch (err) {
      // Silently fail
    }
  }, []);

  const generaNotifiche = async () => {
    setLoading(true);
    try {
      const res = await api.post('/notifiche/genera-automatiche');
      if (res.data.notifiche?.length > 0) {
        toast.success(res.data.message);
      }
      loadNotifiche();
    } catch (err) {
      toast.error('Errore generazione notifiche');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificaId, link) => {
    try {
      await api.put(`/notifiche/${notificaId}/leggi`);
      loadNotifiche();
      if (link) {
        setOpen(false);
        navigate(link);
      }
    } catch (err) {
      // Silently fail
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put('/notifiche/leggi-tutte');
      toast.success('Tutte le notifiche marcate come lette');
      loadNotifiche();
    } catch (err) {
      toast.error('Errore');
    }
  };

  const deleteNotifica = async (notificaId, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/notifiche/${notificaId}`);
      loadNotifiche();
    } catch (err) {
      // Silently fail
    }
  };

  const clearAllRead = async () => {
    try {
      await api.delete('/notifiche');
      toast.success('Notifiche lette eliminate');
      loadNotifiche();
    } catch (err) {
      toast.error('Errore');
    }
  };

  // Load on mount and set interval
  useEffect(() => {
    loadNotifiche();
    // Genera notifiche automatiche al caricamento
    generaNotifiche();
    
    // Inizializza notifiche push
    initPushNotifications();
    
    // Poll ogni 60 secondi
    const interval = setInterval(loadNotifiche, 60000);
    return () => {
      clearInterval(interval);
      notificationService.stopPolling();
    };
  }, [loadNotifiche]);

  const initPushNotifications = async () => {
    await notificationService.init();
    const status = notificationService.getStatus();
    setPushPermission(status.permission);
    setPushEnabled(status.enabled);
    
    // Se già abilitato, avvia polling push
    if (status.permission === 'granted' && status.enabled) {
      notificationService.startPolling(api, handleNewEmails);
    }
  };

  const handleNewEmails = (data) => {
    setHasNewEmail(true);
    setEmailCount(data.new_count);
    setEmailCaselle(data.caselle || []);
    loadNotifiche();
  };

  const requestPushPermission = async () => {
    const result = await notificationService.requestPermission();
    if (result.success) {
      toast.success('Notifiche push abilitate!');
      setPushPermission('granted');
      setPushEnabled(true);
      notificationService.startPolling(api, handleNewEmails);
    } else {
      toast.error(result.message);
    }
  };

  const togglePushNotifications = (enabled) => {
    if (enabled) {
      if (pushPermission !== 'granted') {
        requestPushPermission();
      } else {
        notificationService.startPolling(api, handleNewEmails);
        setPushEnabled(true);
      }
    } else {
      notificationService.stopPolling();
      setPushEnabled(false);
    }
  };

  const testPushNotification = () => {
    notificationService.showNotification('🔔 Test Notifica', {
      body: 'Le notifiche push funzionano correttamente!',
      tag: 'test-' + Date.now()
    });
    toast.success('Notifica di test inviata');
  };

  // Load when popover opens
  useEffect(() => {
    if (open) {
      loadNotifiche();
    }
  }, [open, loadNotifiche]);

  const getIcon = (icona) => {
    const Icon = ICONE[icona] || Bell;
    return Icon;
  };

  const formatTime = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: it });
    } catch {
      return '';
    }
  };

  return (
    <>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative text-slate-300 hover:text-white hover:bg-slate-700"
          data-testid="notifiche-button"
        >
          {hasNewEmail ? (
            <BellRing className="w-5 h-5 text-amber-400 animate-pulse" />
          ) : (
            <Bell className="w-5 h-5" />
          )}
          {(count + emailCount) > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center bg-red-500 text-white text-xs px-1"
            >
              {(count + emailCount) > 99 ? '99+' : count + emailCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-amber-600" />
            <h3 className="font-semibold">Notifiche</h3>
            {count > 0 && <Badge className="bg-red-500">{count}</Badge>}
          </div>
          <div className="flex gap-1">
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8" 
              onClick={generaNotifiche}
              disabled={loading}
              title="Aggiorna notifiche"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            {count > 0 && (
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 text-green-600" 
                onClick={markAllAsRead}
                title="Segna tutte come lette"
              >
                <CheckCheck className="w-4 h-4" />
              </Button>
            )}
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8" 
              onClick={() => { setOpen(false); setSettingsOpen(true); }}
              title="Impostazioni notifiche"
            >
              <Settings className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Sezione Email Non Lette */}
        {emailCount > 0 && (
          <div className="p-3 bg-gradient-to-r from-red-50 via-blue-50 to-green-50 border-b">
            <div 
              className="flex items-center gap-3 cursor-pointer hover:bg-white/50 p-2 rounded-lg transition-colors"
              onClick={() => { setOpen(false); setHasNewEmail(false); navigate('/pec'); }}
            >
              <div className="bg-white p-2 rounded-full shadow-sm">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-sm">{emailCount} Email Non Lette</p>
                <div className="flex gap-2 mt-1">
                  {emailCaselle.filter(c => c.non_lette > 0).slice(0, 3).map((c, idx) => (
                    <Badge 
                      key={idx} 
                      variant="outline" 
                      className={`text-xs ${
                        c.tipo === 'pec' ? 'border-red-300 text-red-600 bg-red-50' :
                        c.tipo === 'standard' ? 'border-blue-300 text-blue-600 bg-blue-50' :
                        'border-green-300 text-green-600 bg-green-50'
                      }`}
                    >
                      {c.nome}: {c.non_lette}
                    </Badge>
                  ))}
                </div>
              </div>
              <BellRing className="w-5 h-5 text-amber-500 animate-pulse" />
            </div>
          </div>
        )}

        {/* Lista notifiche */}
        <ScrollArea className="h-[400px]">
          {notifiche.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Bell className="w-12 h-12 opacity-30 mb-2" />
              <p>Nessuna notifica</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifiche.map((notifica) => {
                const Icon = getIcon(notifica.icona);
                return (
                  <div
                    key={notifica.id}
                    className={`p-3 cursor-pointer transition-colors hover:bg-slate-50 ${!notifica.letta ? 'bg-blue-50' : ''}`}
                    onClick={() => markAsRead(notifica.id, notifica.link)}
                  >
                    <div className="flex gap-3">
                      {/* Icona con indicatore priorità */}
                      <div className="relative flex-shrink-0">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${notifica.letta ? 'bg-slate-100' : 'bg-blue-100'}`}>
                          <Icon className={`w-5 h-5 ${notifica.letta ? 'text-slate-500' : 'text-blue-600'}`} />
                        </div>
                        {!notifica.letta && (
                          <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full ${COLORI_PRIORITA[notifica.priorita] || 'bg-blue-500'}`} />
                        )}
                      </div>

                      {/* Contenuto */}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!notifica.letta ? 'font-semibold' : 'font-medium text-slate-600'}`}>
                          {notifica.titolo}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                          {notifica.messaggio}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatTime(notifica.data_creazione)}
                        </p>
                      </div>

                      {/* Azioni */}
                      <div className="flex flex-col gap-1">
                        {!notifica.letta && (
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6 text-green-600"
                            onClick={(e) => { e.stopPropagation(); markAsRead(notifica.id); }}
                            title="Segna come letta"
                          >
                            <Check className="w-3 h-3" />
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6 text-red-500"
                          onClick={(e) => deleteNotifica(notifica.id, e)}
                          title="Elimina"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        {notifiche.some(n => n.letta) && (
          <div className="p-2 border-t">
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full text-red-600"
              onClick={clearAllRead}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Elimina notifiche lette
            </Button>
          </div>
        )}
        
        {/* Push Status */}
        <div className="p-2 border-t bg-slate-50">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground flex items-center gap-1">
              {pushPermission === 'granted' && pushEnabled ? (
                <>
                  <Bell className="w-3 h-3 text-green-500" />
                  Notifiche push attive
                </>
              ) : (
                <>
                  <BellOff className="w-3 h-3 text-muted-foreground" />
                  Notifiche push disattive
                </>
              )}
            </span>
            {pushPermission !== 'granted' && (
              <Button 
                variant="link" 
                size="sm" 
                className="h-auto p-0 text-xs"
                onClick={requestPushPermission}
              >
                Abilita
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>

    {/* Dialog Impostazioni Push */}
    <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Impostazioni Notifiche Push
          </DialogTitle>
          <DialogDescription>
            Ricevi notifiche sul desktop quando arrivano nuove email
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Supporto browser */}
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium text-sm">Supporto Browser</p>
              <p className="text-xs text-muted-foreground">
                {'Notification' in window ? 'Il tuo browser supporta le notifiche' : 'Browser non supportato'}
              </p>
            </div>
            {'Notification' in window ? (
              <Check className="w-5 h-5 text-green-500" />
            ) : (
              <X className="w-5 h-5 text-red-500" />
            )}
          </div>
          
          {/* Permesso */}
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium text-sm">Permesso Notifiche</p>
              <p className="text-xs text-muted-foreground">
                {pushPermission === 'granted' && '✓ Concesso'}
                {pushPermission === 'denied' && '✗ Negato - Abilita dalle impostazioni del browser'}
                {pushPermission === 'default' && 'Non ancora richiesto'}
              </p>
            </div>
            {pushPermission === 'default' && (
              <Button size="sm" onClick={requestPushPermission}>
                Richiedi
              </Button>
            )}
          </div>
          
          {/* Toggle */}
          {pushPermission === 'granted' && (
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <Label className="font-medium text-sm">Notifiche Push Attive</Label>
                <p className="text-xs text-muted-foreground">
                  Ricevi una notifica quando arrivano nuove email
                </p>
              </div>
              <Switch
                checked={pushEnabled}
                onCheckedChange={togglePushNotifications}
              />
            </div>
          )}
          
          {/* Test */}
          {pushPermission === 'granted' && (
            <Button variant="outline" className="w-full" onClick={testPushNotification}>
              <Bell className="w-4 h-4 mr-2" />
              Invia Notifica di Test
            </Button>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => setSettingsOpen(false)}>
            Chiudi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default NotificheDropdown;
